import React, { useState } from "react";
import "./merchant-page.css";

import MerchantProfile from "components/merchant-profile/MerchantProfile";
import KpiCard from "components/kpi-card/KpiCard";
import RevenueChart from "components/revenue-chart/RevenueChart";
import PeakHours from "components/peak-hours/PeakHours";
import CustomerList from "components/customer-list/CustomerList";
import PaymentItem from "components/payment-item/PaymentItem";
import FilterChipsV1, {
  FilterOption,
} from "components/filter-chips-v1/FilterChipsV1";
import { Skeleton, SkeletonPaymentItem } from "components/skeleton";
import RedeemList from "features/redeem/redeem-list/RedeemList";
import TerminalsList from "features/terminals/terminals-list/TerminalsList";
import { useTerminals } from "features/terminals/useTerminals";
import QrModal from "modals/qr-modal/QrModal";
import LoadingModalV1 from "modals/loading/LoadingModalV1";
import SuccessModal from "modals/success-modal/SuccessModal";
import ErrorModal from "modals/error-modal/ErrorModal";
import TransactionDetailModal from "modals/transaction-detail/TransactionDetailModal";
import { useMerchantPage, TxFilter, KpiSlice } from "./useMerchantPage";
import { useRedeemData } from "features/redeem/useRedeemData";
import { useRefundData } from "features/refund/useRefundData";
import { useSelector } from "react-redux";
import {
  merchantMetadataSelector,
  networkTypeSelector,
} from "store/userSliceHelpers";
import { useSettlementToken } from "features/tokens/useSettlementToken";
import { tokenHoldingsUrl } from "utils/tokens";
import formatDate from "utils/DateUtil";
import { useModal } from "hooks/useModal";
import { Payment } from "types/payment";
import MERCHANT_UTIL from "utils/merchant";
import RefundList from "features/refund/refund-list/RefundList";
import { apiErrorMessage } from "utils/errors/parseApiError";

const TX_FILTER_OPTIONS: FilterOption<TxFilter>[] = [
  { value: "all", label: "All" },
  { value: "in", label: "In" },
  { value: "out", label: "Out" },
];

function MerchantPage() {
  const {
    revenueSeries,
    filteredPayments,
    txFilter,
    setTxFilter,
    peakHours,
    topCustomers,
    kpi,
    address,
    merchantId,
    merchantInfo,
    merchantBalance,
    isMerchantMissing,
    configError,
    areFiguresUnavailable,
    isInfoLoading,
    isBalanceLoading,
    isTxLoading,
    copied,
    handleCopy,
  } = useMerchantPage();
  const merchantMetaData = useSelector(merchantMetadataSelector);
  const networkType = useSelector(networkTypeSelector);
  const {
    selected: selectedToken,
    label: tokenSymbol,
    param: settlementToken,
  } = useSettlementToken();

  // No placeholder identities here. These used to fall back to a demo café name
  // and category, which meant every login flashed another merchant's details
  // before the real ones arrived — indistinguishable from real data. While the
  // info query is in flight MerchantProfile skeletons this block; once it has
  // settled, "—" honestly reports a value the backend did not return.
  const name = merchantInfo?.merchantName;
  const category = merchantMetaData?.merchantDesc;
  const treasuryId = merchantInfo?.treasuryId;

  // merchantMetadata is only written at login, so a session restored from the
  // cookie can reach this page without it. formatDate hands the value straight
  // to date-fns, which throws on a missing one — so skip the call entirely.
  const memberSince = merchantMetaData?.createdAt
    ? formatDate(merchantMetaData.createdAt)
    : "—";

  const {
    requests,
    pendingRedeemsBalance,
    requestsLoading,
    refreshRequests,
    submitRedeem,
  } = useRedeemData(merchantId);
  // Both terms are now in the SELECTED token: the balance comes from ?token= and the pending
  // total is filtered to the same token inside useRedeemData.
  const lifetimeAmount = merchantBalance - pendingRedeemsBalance;

  const {
    refunds,
    refundsLoading,
    refreshRefunds,
    pendingCount,
    settleRefund,
    rejectRefund,
  } = useRefundData(merchantId);

  const { terminals, loading: terminalsLoading } = useTerminals(merchantId);

  // ── Modal state (settle / reject) ──────────────────────────────────
  const {
    showModal: showQr,
    openModal: openQrModal,
    closeModal: closeQrModal,
  } = useModal();
  const {
    showModal: showPaymentDetail,
    openModal: openPaymentDetailModal,
    closeModal: closePaymentDetailModal,
  } = useModal();
  const {
    showModal: showLoading,
    openModal: openLoadingModal,
    closeModal: closeLoadingModal,
  } = useModal();
  const {
    showModal: showSuccess,
    openModal: openSuccessModal,
    closeModal: closeSuccessModal,
  } = useModal();
  const {
    showModal: showError,
    openModal: openErrorModal,
    closeModal: closeErrorModal,
  } = useModal();

  const [currentTransaction, setCurrentTransaction] = useState<Payment>();
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [loadingMsg, setLoadingMsg] = useState("Processing…");
  const [processingId, setProcessingId] = useState<string | null>(null);

  function handleOpenPaymentDetail(payment: Payment) {
    setCurrentTransaction(payment);
    openPaymentDetailModal();
  }

  function handleSettle(
    requestId: string,
    merchantId: string,
    cardNumber: string,
  ) {
    setProcessingId(requestId);
    setLoadingMsg("Processing…");
    openLoadingModal();
    settleRefund(
      { requestId, merchantId, cardNumber },
      {
        onSuccess: () => {
          setProcessingId(null);
          closeLoadingModal();
          setSuccessMsg("Return settled successfully.");
          openSuccessModal();
        },
        onError: (err: any) => {
          setProcessingId(null);
          closeLoadingModal();
          setErrorMsg(
            apiErrorMessage(err, "Failed to settle return. Please try again."),
          );
          openErrorModal();
        },
      },
    );
  }

  function handleReject(
    requestId: string,
    merchantId: string,
    cardNumber: string,
  ) {
    setProcessingId(requestId);
    setLoadingMsg("Processing…");
    openLoadingModal();
    rejectRefund(
      { requestId, merchantId, cardNumber, reason: 0 },
      {
        onSuccess: () => {
          setProcessingId(null);
          closeLoadingModal();
          setSuccessMsg("Return rejected.");
          openSuccessModal();
        },
        onError: (err: any) => {
          setProcessingId(null);
          closeLoadingModal();
          setErrorMsg(
            apiErrorMessage(err, "Failed to reject return. Please try again."),
          );
          openErrorModal();
        },
      },
    );
  }

  // With no merchant on this network the figures aren't zero, they're unknown —
  // so show a dash rather than a confident 0.00.
  const renderKpi = (scope: string, dotColor: string, slice: KpiSlice) => (
    <KpiCard
      scope={scope}
      dotColor={dotColor}
      amount={
        isTxLoading ? (
          <Skeleton width={96} height={26} />
        ) : areFiguresUnavailable ? (
          "—"
        ) : (
          slice.amount
        )
      }
      unit={isTxLoading || areFiguresUnavailable ? undefined : tokenSymbol}
      subText={
        isTxLoading ? (
          <Skeleton width={80} height={11} />
        ) : areFiguresUnavailable ? (
          "No data"
        ) : (
          slice.subText
        )
      }
      delta={isTxLoading || areFiguresUnavailable ? undefined : slice.delta}
      deltaDir={
        isTxLoading || areFiguresUnavailable ? undefined : slice.deltaDir
      }
    />
  );

  return (
    <div className="merchant-page">
      <MerchantProfile
        logoLetter={name?.charAt(0).toUpperCase() ?? ""}
        name={name ?? "—"}
        category={category ?? "—"}
        status={MERCHANT_UTIL.getMerchantStatus(merchantInfo?.merchantStatus)}
        memberSince={memberSince}
        infoLoading={isInfoLoading}
        address={address}
        lifetimeAmount={isMerchantMissing ? "—" : lifetimeAmount.toFixed(2)}
        lifetimeUnit={tokenSymbol}
        balanceLoading={isBalanceLoading}
        copied={copied}
        onCopy={handleCopy}
        onExplorer={() =>
          window.open(
            tokenHoldingsUrl(networkType, selectedToken?.address, address),
            "_blank",
          )
        }
        onQr={openQrModal}
        merchantId={merchantId}
        treasuryId={treasuryId}
        balance={lifetimeAmount}
        tokenSymbol={tokenSymbol}
        settlementToken={settlementToken}
        pendingCount={pendingCount}
        // Both redeem guards read stale zeros until these land, which matters
        // on the Stripe-onboarding return — that path opens the redeem modal
        // on page load rather than on a click.
        guardsLoading={refundsLoading || requestsLoading || isBalanceLoading}
        submitRedeem={submitRedeem}
      />

      <section className="merchant-page__section">
        <TerminalsList
          terminals={terminals}
          loading={terminalsLoading}
          merchantId={merchantId}
        />
      </section>

      {/* Config failed: a redeem can't be told from a refund, so every netted
          figure below is unreliable. Say so rather than dash the numbers silently. */}
      {configError ? (
        <div className="merchant-page__config-error" role="alert">
          {configError} Payment figures are unavailable until it loads.
        </div>
      ) : null}

      <div className="merchant-page__kpi-grid">
        {renderKpi("Last 24h", "var(--pos)", kpi.last24h)}
        {renderKpi("Last 7 days", "var(--brand-hi)", kpi.last7d)}
        {renderKpi("Last 30 days", "var(--copper)", kpi.last30d)}
      </div>

      <RevenueChart
        title="Revenue · Last 30 days"
        meta="Last 30 days"
        data={revenueSeries}
        unit={tokenSymbol}
        loading={isTxLoading}
      />

      <div className="merchant-page__split">
        <PeakHours
          hours={peakHours}
          title="Peak hours"
          meta="Last 24h"
          loading={isTxLoading}
        />
        <CustomerList
          customers={topCustomers}
          title="Top customers"
          meta="Last 30 days"
          unit={tokenSymbol}
          loading={isTxLoading}
        />
      </div>

      <section className="merchant-page__section">
        <RefundList
          refunds={refunds}
          loading={refundsLoading}
          processingId={processingId}
          fallbackTokenSymbol={tokenSymbol}
          onRefresh={refreshRefunds}
          onSettle={handleSettle}
          onReject={handleReject}
        />
      </section>

      <section className="merchant-page__section">
        <RedeemList
          requests={requests}
          loading={requestsLoading}
          fallbackTokenSymbol={tokenSymbol}
          onRefresh={refreshRequests}
        />
      </section>

      <section className="merchant-page__section">
        <div className="merchant-page__section-header">
          <h3 className="merchant-page__section-title">Recent payments</h3>
          <FilterChipsV1
            options={TX_FILTER_OPTIONS}
            value={txFilter}
            onChange={setTxFilter}
          />
        </div>
        <div className="merchant-page__pay-list">
          {isTxLoading ? (
            Array.from({ length: 4 }, (_, i) => <SkeletonPaymentItem key={i} />)
          ) : filteredPayments.length === 0 ? (
            <div className="merchant-page__empty">No recent payments.</div>
          ) : (
            filteredPayments.map((p) => (
              <PaymentItem
                key={p.txId}
                payment={p}
                tokenSymbol={tokenSymbol}
                onClick={handleOpenPaymentDetail}
              />
            ))
          )}
        </div>
      </section>

      {showQr && (
        <QrModal
          isOpen={showQr}
          data={address}
          close={closeQrModal}
          warning={`Please make sure the amount you send is in ${tokenSymbol} — otherwise the funds will be lost.`}
        />
      )}
      {showPaymentDetail && currentTransaction && (
        <TransactionDetailModal
          isOpen={showPaymentDetail}
          transaction={currentTransaction}
          tokenSymbol={tokenSymbol}
          handleClose={closePaymentDetailModal}
        />
      )}
      <LoadingModalV1 isOpen={showLoading} loadingMsg={loadingMsg} />
      {showSuccess && (
        <SuccessModal
          isOpen={showSuccess}
          message={successMsg}
          handleClose={closeSuccessModal}
        />
      )}
      {showError && (
        <ErrorModal
          isOpen={showError}
          errorMsg={errorMsg}
          handleClose={closeErrorModal}
        />
      )}
    </div>
  );
}

export default MerchantPage;
